import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Button, Spin, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import LayoutWrapper from '@iso/components/utility/layoutWrapper.js';
import { taskService, propertyService } from '@iso/services';
import { commonHelper } from '@iso/lib/helpers/commonHelper';
import { propertyCenter } from '@iso/lib/helpers/googleApiHelper';
import modalActions from '@iso/redux/modal/actions';
import NewTask from '@iso/containers/Pages/Task/NewTask';
import TaskModal from '@iso/containers/Pages/Task/TaskModal';
import { filterTasks, parseTags, TB_COLORS } from './utils';
import FilterBar from './FilterBar';
import ListView from './ListView';
import BoardView from './BoardView';
import MapView from './MapView';
import CalendarView from './CalendarView';

const { openModal } = modalActions;

const VIEWS = [
  { key: 'list', label: 'List' },
  { key: 'board', label: 'Board' },
  { key: 'map', label: 'Map' },
  { key: 'calendar', label: 'Calendar' },
];

const EMPTY_FILTERS = { assignee: '', priority: '', status: '', tag: '', dateRange: null, search: '' };

const TaskBoard = () => {
  const dispatch = useDispatch();
  const activePropertyId = useSelector((state) => state.property.activePropertyId);
  const currentUser = useSelector((state) => state.user.user);
  const activeProperty = commonHelper.getActiveProperty();
  const propertyName = activeProperty?.name;

  const [tasks, setTasks] = useState([]);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('list');
  // Default the assignee filter to the current user so they land on their own
  // tasks; they can clear it to see the whole property.
  const [filters, setFilters] = useState(() => ({
    ...EMPTY_FILTERS,
    assignee: currentUser?.id ? String(currentUser.id) : '',
  }));
  const [newTaskVisible, setNewTaskVisible] = useState(false);
  // Apply the "my tasks" default once, in case currentUser hydrates after the
  // first render. Only runs while the filter is still untouched (empty).
  const defaultedRef = useRef(!!currentUser?.id);
  useEffect(() => {
    if (!defaultedRef.current && currentUser?.id) {
      defaultedRef.current = true;
      setFilters((f) => (f.assignee ? f : { ...f, assignee: String(currentUser.id) }));
    }
  }, [currentUser]);

  const reload = useCallback(() => {
    if (!activePropertyId) return;
    setLoading(true);
    taskService
      .getList(activePropertyId)
      .then((res) => setTasks(res?.tasks || []))
      .catch(() => message.error('Could not load tasks'))
      .finally(() => setLoading(false));
  }, [activePropertyId]);

  useEffect(() => {
    reload();
    if (activePropertyId) {
      propertyService.viewProperty(activePropertyId).then((res) => {
        if (res && res.code === '0000') setAreas(res.primary_objects || []);
      });
    }
  }, [activePropertyId, reload]);

  const areaMap = useMemo(() => {
    const m = {};
    areas.forEach((a) => {
      m[a.id] = a.name;
    });
    return m;
  }, [areas]);

  const assigneeOptions = useMemo(() => {
    const seen = {};
    // Always include the current user so the "my tasks" default filter shows
    // their name (not a bare id) even when they have no tasks assigned yet.
    if (currentUser?.id && currentUser?.name) {
      seen[currentUser.id] = { id: currentUser.id, name: currentUser.name };
    }
    tasks.forEach((t) => {
      if (t.assignee && t.assignee.id && t.assignee.name && !seen[t.assignee.id]) {
        seen[t.assignee.id] = { id: t.assignee.id, name: t.assignee.name };
      }
    });
    return Object.values(seen).sort((a, b) => String(a.name).localeCompare(b.name));
  }, [tasks, currentUser]);

  const tagOptions = useMemo(() => {
    const set = new Set();
    tasks.forEach((t) => parseTags(t.tags).forEach((tag) => set.add(tag)));
    return Array.from(set).sort();
  }, [tasks]);

  const filtered = useMemo(() => filterTasks(tasks, filters, areaMap), [tasks, filters, areaMap]);

  // Open the existing task detail/edit modal (Redux-driven), matching how the
  // legacy board opens it — with an `assignees` array derived from the single
  // assignee so the modal populates cleanly.
  const onEdit = useCallback(
    (task) => {
      dispatch(
        openModal({
          type: 'task',
          task: { ...task, assignees: task.assignee && task.assignee.id ? [task.assignee] : [] },
        })
      );
    },
    [dispatch]
  );

  const onChangeStatus = useCallback(
    (task, status) => {
      // Optimistic update; the legacy change-status endpoint is fire-and-forget.
      setTasks((prev) => prev.map((t) => (t.id === task.id ? { ...t, status } : t)));
      taskService.changeStatus({ task: { status, position: task.position || 0 } }, task.id);
      setTimeout(reload, 600);
    },
    [reload]
  );

  const onToggleComplete = useCallback(
    (task, checked) => onChangeStatus(task, checked ? 'completed' : 'todo'),
    [onChangeStatus]
  );

  const renderView = () => {
    switch (view) {
      case 'board':
        return <BoardView tasks={filtered} areaMap={areaMap} propertyName={propertyName} onEdit={onEdit} onChangeStatus={onChangeStatus} />;
      case 'map':
        return (
          <MapView
            tasks={filtered}
            areaMap={areaMap}
            propertyName={propertyName}
            onEdit={onEdit}
            propertyCenter={propertyCenter(activeProperty)}
          />
        );
      case 'calendar':
        return <CalendarView tasks={filtered} areaMap={areaMap} propertyName={propertyName} onEdit={onEdit} />;
      case 'list':
      default:
        return <ListView tasks={filtered} areaMap={areaMap} propertyName={propertyName} onEdit={onEdit} onToggleComplete={onToggleComplete} />;
    }
  };

  return (
    <LayoutWrapper style={{ display: 'block' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
        <h1 style={{ margin: 0, fontWeight: 700, fontSize: 28, color: TB_COLORS.text }}>Task Board</h1>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setNewTaskVisible(true)}>
          New Task
        </Button>
      </div>

      {/* View switch */}
      <div style={{ display: 'inline-flex', gap: 8, marginBottom: 16 }}>
        {VIEWS.map((v) => {
          const active = view === v.key;
          return (
            <button
              key={v.key}
              onClick={() => setView(v.key)}
              style={{
                border: `1px solid ${active ? '#0B2A1B' : '#eaecf0'}`,
                background: active ? '#0B2A1B' : '#fff',
                color: active ? '#fff' : TB_COLORS.subtext,
                fontWeight: 600,
                fontSize: 14,
                padding: '8px 20px',
                borderRadius: 8,
                cursor: 'pointer',
              }}
            >
              {v.label}
            </button>
          );
        })}
      </div>

      <FilterBar
        filters={filters}
        setFilters={setFilters}
        assigneeOptions={assigneeOptions}
        tagOptions={tagOptions}
        onClear={() => setFilters(EMPTY_FILTERS)}
      />

      <Spin spinning={loading}>{renderView()}</Spin>

      <NewTask
        visibleNewTaskModal={newTaskVisible}
        setVisibleNewTaskModal={setNewTaskVisible}
        primaryObjects={areas}
        propertyId={activePropertyId}
        onSaved={reload}
        kanbanToggle={-1}
      />
      <TaskModal
        propertyId={activePropertyId}
        onSaved={reload}
        updateDataBoard={reload}
        primaryObjects={areas}
        kanbanToggle={-1}
      />
    </LayoutWrapper>
  );
};

export default TaskBoard;
